import { ChildModel, ModelBase } from 'store/model-base';
import { DataPlanType, LineStatus, UnlPlanSubgroup } from 'my-account/constants';
import { LineSummary } from './line-summary';
import { SortOptions } from 'core/constants';

export class PlanSummary extends ModelBase {
    public static storeName: string = 'planSummary';

    public bucketId: string;
    public planType: DataPlanType;
    public planPrice: number;
    public dataUsage: number;
    public dataCost: number;
    public dataOverage: number;
    public isLegacy: boolean;
    public dataPassSize: number;
    public overagePrice: number;
    public overageCost: number;
    public lines: LineSummary[];

    public isUnlimited: boolean;
    public isTablet: boolean;
    public isByTheGigShared: boolean;
    public isByTheGig: boolean;
    public isWatch: boolean;
    public allLinesPending: boolean;
    public showAllLines: boolean = false;
    public sortedLines: LineSummary[] = this.sort(SortOptions.A_Z);

    public static create<T extends ModelBase>(data: ApiResponse): T {
        data.isUnlimited = data.planType === DataPlanType.UNLIMITED;
        data.isTablet = data.planType === DataPlanType.TABLET;
        data.isByTheGigShared = data.planType === DataPlanType.BY_THE_GIG_SHARED;
        data.isByTheGig = data.planType === DataPlanType.BY_THE_GIG;
        data.isWatch = data.planType === DataPlanType.SMART_WATCH;
        data.allLinesPending = Boolean(data.lines.every((line: LineSummary) => line.status === LineStatus.PENDING));

        data.lines = data.lines.map((line: LineSummary): LineSummary => {
            line.isLegacy = data.isLegacy;
            line.isUnlimited = data.isUnlimited;

            return line;
        });

        const toReturn: PlanSummary = super.create<PlanSummary>(data);

        /* eslint-disable @typescript-eslint/no-explicit-any */
        return <T> <any> toReturn;
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    protected static get hasMany(): ChildModel[] {
        return [{
            attrName: 'lines',
            model: LineSummary
        }];
    }

    public get isNoTabletPlan(): boolean {
        return this.lines.every((line: LineSummary) => !line.isTablet);
    }

    public get isTabletOnlyPlan(): boolean {
        return this.lines.every((line: LineSummary) => line.isTablet);
    }

    public sort(sortOption: string): LineSummary[] {
        switch (sortOption) {
            case SortOptions.A_Z:
                return this.lines.sort((a: LineSummary, b: LineSummary) => {
                    if (a.displayName < b.displayName) {
                        return -1;
                    } else if (a.displayName > b.displayName) {
                        return 1;
                    } else return 0;
                });
            case SortOptions.NEWEST:
                const sortedList: LineSummary[] = [...this.lines.filter((line: LineSummary) =>
                    line.status === LineStatus.READY_FOR_ACTIVATION
                ), ...this.lines.filter((line: LineSummary) =>
                    line.status === LineStatus.PENDING
                ), ...this.lines.filter((line: LineSummary) =>
                    line.status === LineStatus.ACTIVE
                ), ...this.lines.filter((line: LineSummary) =>
                    line.status === LineStatus.DEACTIVATED
                ), ...this.lines.filter((line: LineSummary) =>
                    line.status !== LineStatus.READY_FOR_ACTIVATION
                    && line.status !== LineStatus.PENDING
                    && line.status !== LineStatus.ACTIVE
                    && line.status !== LineStatus.DEACTIVATED
                )];

                return sortedList;
            case SortOptions.MOST_DATA:
                return this.lines.sort((a: LineSummary, b: LineSummary) => b.dataUsage - a.dataUsage);
            case SortOptions.LEAST_DATA:
                return this.lines.sort((a: LineSummary, b: LineSummary) => a.dataUsage - b.dataUsage);
        }
    }

    public get unlIntroLines(): LineSummary[] {
        return this.lines.filter((line: LineSummary) => line.planSubgroup === UnlPlanSubgroup.BASIC);
    }

    public get unlPlusLines(): LineSummary[] {
        return this.lines.filter((line: LineSummary) => line.planSubgroup === UnlPlanSubgroup.PLUS);
    }

    public get unlPremiumLines(): LineSummary[] {
        return this.lines.filter((line: LineSummary) => line.planSubgroup === UnlPlanSubgroup.PREMIUM);
    }

    public get unlProLines(): LineSummary[] {
        return this.lines.filter((line: LineSummary) => line.planSubgroup === UnlPlanSubgroup.PRO);
    }

    public get unlStandardLines(): LineSummary[] {
        return this.lines.filter((line: LineSummary) => line.planSubgroup === UnlPlanSubgroup.STANDARD);
    }

    public get unlSelectLines(): LineSummary[] {
        return this.lines.filter((line: LineSummary) => line.planSubgroup === UnlPlanSubgroup.SELECT);
    }
}
